import { Container, Grid } from '@material-ui/core';
import { AuthContext } from 'auth/AuthContext';
import authService from 'auth/AuthService';
import { LoginBusiness } from 'business/business/LoginBusiness';
import { IO } from 'business/io/IO';
import {
  BaseModal,
  ButtonBase,
  Loader,
  SelectBase,
} from 'components';
import { abreviaturas, masterMessages, messages } from 'constantes';
import { Field, Form, Formik } from 'formik';
import Cookie from 'js-cookie';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { withNotification } from 'wrappers';
import * as Yup from 'yup';

/**
 * @desc Objeto que evalua si los campos del formulario son correctos
 */
const validationSchema = Yup.object({
  companiaSelected: Yup.string().required('La compañia es requerida'),
});

/**
 * @name KeycloackInterceptor
 * @desc Encargada de manejar la respuesta de logueo de Keycloack
 * @param appError
 * @returns {JSX.Element}
 * @constructor
 */
const KeycloackInterceptor = ({ appError }) => {
  const history = useHistory();
  const location = useLocation();
  const username = Cookie.get(abreviaturas.USERNAME);
  const solicitudId = Cookie.get(abreviaturas.DEFAULT_SOLICITUD_ID);
  // Mensajes a mostrar
  const {
    auth: { modalKeycloack: formMessages }, buttons,
  } = masterMessages;
  const { dispatch } = useContext(AuthContext);
  const [loading, setLoading] = useState(true); // Cambiar valores despues de terminar
  const [openModal, setOpenModal] = useState(false);
  const [companias, setCompanias] = useState([]);
  const values = { companiaSelected: '' };
  const { companiaSelected } = values || {};

  const getQueryParam = (param) => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get(param);
  };

  /**
   * @name getOptionItems
   * @desc Funcion para armar el array a mostrar en slectBase
   * @param data
   * @returns {*}
   */
  const getOptionItems = (data) => data.map((item) => (
    { value: item.id, label: item.nombreCompania } // Depende de lo que llegue en el OBJ
  ));

  /**
   * @name getToken
   * @desc Se encarga de traer los token y almacenarlos para realizar la redirección de ruta
   * @param idCompania
   * @returns {Promise<void>}
   */
  const getToken = async (idCompania) => {
    setLoading(true);
    const data = {
      idCompania,
      userName: username,
    };

    // llamada al api
    try {
      const tokenRes = await LoginBusiness.obtenerTokenKeycloak(data);
      const dataJson = tokenRes.json();

      dataJson.then((res) => {
        if (res.body !== null) {
          dispatch({
            type: abreviaturas.TYPE_LOGIN,
            payload: true,
          });
          authService.storeSessionData(res.body);
          IO.BASE_TOKEN = res.body.tokenApp || '';
          IO.BASE_TOKEN_KIOSCO = res.body.tokenKiosco || '';

          // Redirección
          history.push(`/apiv1/validation/${solicitudId}`);
        }
      });
    } catch (error) {
      history.push('/apiv1/sign-in');
      appError(messages.errors.login);
    }
  };

  /**
   * @name getCompanias
   * @desc Encargado de consultar las compañias y almacenar en array
   * @param userName
   * @returns {Promise<void>}
   */
  const getCompanias = async (userName) => {
    const dataCompanias = await LoginBusiness.obtenerCompaniasUsuario(
      userName, abreviaturas.DEFAULT_CONTET_TYPE_TEXT,
    );
    const dataJson = dataCompanias.json();

    dataJson.then((res) => {
      setLoading(false);
      try {
        if (res.body.length === 1) {
          const idCompania = res.body[0].id;
          getToken(idCompania);
        } else if (res.body.length > 1) {
          const arrayCompania = getOptionItems(res.body);
          setCompanias(arrayCompania);
          setOpenModal(true);
          setLoading(false);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log(error);
      }
    });
  };

  useEffect(() => {
    const loginKKCallback = async () => {
      try {
        const code = getQueryParam('code');
        const payload = {
          username,
          code,
          frontOrigin: `${window.location.origin}/apiv1/keycloackLogin`,
        };
        const dataLogin = await LoginBusiness.fetchKCLoginToken(payload);
        const { access_token: tokenKeycloack, backendUrl } = dataLogin || {};
        const isEquals = await authService.isUserEquals(tokenKeycloack, username);

        if (isEquals) {
          // Se almacenan valores
          IO.BASE_API_URL = `${backendUrl}${abreviaturas.DEFAULT_SERVICES}`;
          IO.BASE_TOKEN_KEYCLOACK = tokenKeycloack;

          // Solicitud de compañias
          await getCompanias(username);
        } else {
          history.push('/apiv1/sign-in');
          appError(messages.errors.login);
        }
      } catch (error) {
        history.push('/apiv1/sign-in');
        appError(messages.errors.login);
      }
    };
    loginKKCallback();
  }, [history]);

  // Eventos de modales
  const handleCloseModal = () => {
    setOpenModal(false);
  };

  /**
   * @name onSubmit
   * @desc Destructura el valor seleccionado y llama a la función correspondiente
   * @param formValues
   */
  const onSubmit = (formValues) => {
    const { companiaSelected: selectId } = formValues || {};

    // Se solicita el token mediante el id de compañia
    getToken(selectId);
  };

  /**
   * @name onChangeCompania
   * @desc Funcion que cambia la compañia seleccionada y la setea a la variable del form
   * @param event
   * @param formikBag
   * @returns {Promise<void>}
   */
  const onChangeCompania = async (event, formikBag) => {
    const { value } = event.target;
    formikBag.setValues({
      ...formikBag.values,
      companiaSelected: value,
    });
  };

  return loading ? <Loader /> : (
    <>
      {Boolean(openModal) && (
        <BaseModal
          open
          onClose={handleCloseModal}
          title={formMessages.title}
          message={formMessages.message}
          maxWidth="xs"
          close={false}
        >
          <Grid container>
            <Grid item xs={12} md={12}>
              <Container>
                <Formik
                  enableReinitialize
                  initialValues={{ companiaSelected }}
                  validationSchema={validationSchema}
                  onSubmit={onSubmit}
                >
                  {(subProps) => (
                    <Form>
                      <Grid container alignItems="center" justifyContent="center" spacing={2}>
                        <Grid item xs={12} md={12}>
                          <Field
                            label="Compañia"
                            name="companiaSelected"
                            component={SelectBase}
                            items={companias}
                            onChange={(event) => onChangeCompania(event, subProps)}
                          />
                        </Grid>
                        <Grid container alignItems="center" justifyContent="center" style={{ marginBottom: 15 }}>
                          <Grid item xs={6} md={6}>
                            <ButtonBase
                              disabled={subProps.isSubmitting}
                              caption={buttons.save}
                              colorBase={abreviaturas.DEFAULT_RED_COLOR}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                    </Form>
                  )}
                </Formik>
              </Container>
            </Grid>
          </Grid>
        </BaseModal>
      )}
    </>
  );
};

KeycloackInterceptor.propTypes = {
  appError: PropTypes.func.isRequired,
};

export default withNotification(KeycloackInterceptor);
